import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Native disclosure navigation and the live count for the marketplace header. */
class EBHeader extends Component {
  #controller;
  #resizeObserver;
  #desktopScrollMedia;
  /** @type {AbortController | null} */
  #historyCartController = null;
  #cartRenderRevision = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.addEventListener('keydown', this.onKeydown, { signal });
    this.addEventListener('click', this.onNavigationClick, { signal });
    this.addEventListener('toggle', this.onSearchToggle, { capture: true, signal });
    this.addEventListener('focusout', this.onSearchFocusout, { signal });
    document.addEventListener('click', this.onOutsideClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate, { signal });
    window.addEventListener('pageshow', this.onPageShow, { signal });
    this.#desktopScrollMedia = window.matchMedia('(min-width: 990px)');
    this.#desktopScrollMedia.addEventListener('change', this.onScrollContainerChange, { signal });
    window.addEventListener('scroll', this.onScrollContainerChange, { passive: true, signal });
    window.addEventListener('hashchange', this.onHashNavigation, { signal });
    this.#resizeObserver = new ResizeObserver(this.updateHeaderHeight);
    this.#resizeObserver.observe(this);
    this.updateHeaderHeight();
    this.onScrollContainerChange();
    this.onHashNavigation();

    // History navigation can finish before this module upgrades the custom element.
    const navigation = performance.getEntriesByType('navigation')[0];
    if (navigation instanceof PerformanceNavigationTiming && navigation.type === 'back_forward') {
      this.#reconcileHistoryCart();
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.updateHeaderHeight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#resizeObserver?.disconnect();
    this.#historyCartController?.abort();
    this.#historyCartController = null;
  }

  updateHeaderHeight = () => {
    const height = `${this.offsetHeight}px`;
    document.body.style.setProperty('--header-height', height);
    document.body.style.setProperty('--header-group-height', height);
    const menu = this.refs.mobileMenu;
    const desktopSearchMenu = this.refs.desktopSearchMenu;
    const isDesktop = window.matchMedia('(min-width: 1100px)').matches;
    if (isDesktop && menu instanceof HTMLDetailsElement) menu.open = false;
    if (!isDesktop && desktopSearchMenu instanceof HTMLDetailsElement) desktopSearchMenu.open = false;
  };

  /** Desktop anchors and history can scroll the document; keep Horizon's wrapper as the scroll owner. */
  onScrollContainerChange = () => {
    if (!this.#desktopScrollMedia?.matches || window.scrollY === 0) return;
    const wrapper = this.closest('.page-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;
    const nextOffset = wrapper.scrollTop + window.scrollY;
    window.scrollTo({ top: 0, behavior: 'instant' });
    wrapper.scrollTo({ top: nextOffset, behavior: 'instant' });
  };

  /** Scroll only Horizon's desktop container, keeping its sticky header in view. */
  #scrollToHash = (hash, smooth = false) => {
    if (!this.#desktopScrollMedia?.matches || !hash || hash === '#') return false;
    const wrapper = this.closest('.page-wrapper');
    if (!(wrapper instanceof HTMLElement)) return false;
    let targetId;
    try {
      targetId = decodeURIComponent(hash.slice(1));
    } catch {
      return false;
    }
    const target = document.getElementById(targetId);
    if (!(target instanceof HTMLElement) || !wrapper.contains(target)) return false;
    const top = Math.max(0, wrapper.scrollTop + target.getBoundingClientRect().top - wrapper.getBoundingClientRect().top - this.offsetHeight - 24);
    const behavior = smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant';
    window.scrollTo({ top: 0, behavior: 'instant' });
    wrapper.scrollTo({ top, behavior });
    return true;
  };

  /** Initial, direct and history fragment navigation must target the same scroll owner. */
  onHashNavigation = () => {
    this.#scrollToHash(window.location.hash);
  };

  /** @param {KeyboardEvent} event */
  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const desktopSearchMenu = this.refs.desktopSearchMenu;
    if (desktopSearchMenu instanceof HTMLDetailsElement && desktopSearchMenu.open) {
      desktopSearchMenu.open = false;
      desktopSearchMenu.querySelector('summary')?.focus();
      event.preventDefault();
      return;
    }
    const menu = this.refs.mobileMenu;
    if (!(menu instanceof HTMLDetailsElement) || !menu.open) return;
    menu.open = false;
    menu.querySelector('summary')?.focus();
    event.preventDefault();
  };

  /** @param {MouseEvent} event */
  openSearch(event) {
    const menu = this.refs.mobileMenu;
    const search = this.refs.mobileSearch;
    if (!(menu instanceof HTMLDetailsElement) || !(search instanceof HTMLInputElement)) return;
    event.preventDefault();
    menu.open = true;
    search.focus();
  }

  /** Native disclosure remains usable before JavaScript loads. @param {Event} event */
  onSearchToggle = (event) => {
    const menu = this.refs.desktopSearchMenu;
    const search = this.refs.desktopSearch;
    if (event.target === menu && menu instanceof HTMLDetailsElement && menu.open && search instanceof HTMLInputElement) {
      search.focus();
    }
  };

  /** Keep the non-modal search panel out of the way after keyboard navigation. @param {FocusEvent} event */
  onSearchFocusout = (event) => {
    const menu = this.refs.desktopSearchMenu;
    if (menu instanceof HTMLDetailsElement && event.relatedTarget instanceof Node && !menu.contains(event.relatedTarget)) {
      menu.open = false;
    }
  };

  /** @param {MouseEvent} event */
  onOutsideClick = (event) => {
    const desktopSearchMenu = this.refs.desktopSearchMenu;
    if (event.target instanceof Node && desktopSearchMenu instanceof HTMLDetailsElement && !desktopSearchMenu.contains(event.target)) desktopSearchMenu.open = false;
    const menu = this.refs.mobileMenu;
    if (event.target instanceof Node && !this.contains(event.target) && menu instanceof HTMLDetailsElement) menu.open = false;
  };

  /** Close the mobile panel before following a navigation or in-page FAQ link. @param {MouseEvent} event */
  onNavigationClick = (event) => {
    const menu = this.refs.mobileMenu;
    const link = event.target instanceof Element ? event.target.closest('nav a') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    if (menu instanceof HTMLDetailsElement) menu.open = false;
    if (event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const destination = new URL(link.href);
    if (destination.origin !== window.location.origin || destination.pathname !== window.location.pathname || !destination.hash) return;
    if (!this.#scrollToHash(destination.hash, true)) return;
    event.preventDefault();
    if (destination.hash !== window.location.hash) {
      const nextUrl = new URL(window.location.href);
      nextUrl.hash = destination.hash;
      history.pushState(history.state, '', nextUrl);
    }
  };

  /** @param {PageTransitionEvent} event */
  onPageShow = (event) => {
    this.onScrollContainerChange();
    if (!event.persisted) this.onHashNavigation();
    if (event.persisted) this.#reconcileHistoryCart();
  };

  /** Refresh history-restored markup without overwriting a newer settled cart event. */
  #reconcileHistoryCart = async () => {
    this.#historyCartController?.abort();
    const controller = new AbortController();
    const revision = this.#cartRenderRevision;
    this.#historyCartController = controller;

    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (controller.signal.aborted || this.#historyCartController !== controller || !this.isConnected) return;
      if (revision !== this.#cartRenderRevision || !Number.isInteger(cart?.item_count) || cart.item_count < 0) return;
      if (this.refs.cartCount instanceof HTMLElement) this.refs.cartCount.textContent = String(cart.item_count);
    } catch {
      // Keep the last confirmed count if history reconciliation is cancelled or unavailable.
    } finally {
      if (this.#historyCartController === controller) this.#historyCartController = null;
    }
  };

  /** Wait for Shopify's settled cart event before reading its authoritative quantity. */
  onCartUpdate = async (event) => {
    const signal = this.#controller?.signal;
    try {
      const result = await event.promise;
      if (!result || signal?.aborted) return;
      const count = result.cart?.totalQuantity ?? result.detail?.itemCount;
      if (Number.isInteger(count) && count >= 0 && this.refs.cartCount instanceof HTMLElement) {
        this.#cartRenderRevision += 1;
        this.#historyCartController?.abort();
        this.refs.cartCount.textContent = String(count);
      }
    } catch {
      // Failed cart changes keep the last confirmed count.
    }
  };
}

if (!customElements.get('eb-header')) customElements.define('eb-header', EBHeader);
